import { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { examCandidateAPI } from "@/services/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Check,
  Clock,
  AlertTriangle,
  Camera,
  FileVideo,
  MonitorUp,
  Eye,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import FaceDetectionCamera from "./FaceDetectionCamera";

const ExamInstructions = () => {
  const { assignmentId } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();

  // State management
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [startingExam, setStartingExam] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState({
    camera: false,
    audio: false,
  });
  const [faceDetected, setFaceDetected] = useState(false);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [permissionType, setPermissionType] = useState("");

  // Fetch exam data
  const {
    data: examData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["exam-details", assignmentId],
    queryFn: () => examCandidateAPI.getExamDetails(assignmentId),
    enabled: !!assignmentId,
    staleTime: 5 * 60 * 1000,
  });

  const assignment = examData?.data?.assignment;
  const exam = examData?.data?.exam;

  // Check required permissions based on proctoring settings
  const isProctoring = exam?.proctoring?.enabled;
  const requiresCamera = isProctoring && exam?.proctoring?.captureImages;
  const requiresAudio = isProctoring && exam?.proctoring?.captureAudio;
  const requiresFaceDetection = isProctoring && exam?.proctoring?.faceDetection;


  // Request permissions on component mount
  useEffect(() => {
    const requestPermissions = async () => {
      if (requiresCamera) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          setPermissionStatus((prev) => ({ ...prev, camera: true }));
          stream.getTracks().forEach((track) => track.stop());
        } catch (error) {
          console.error("Camera permission error:", error);
          setPermissionStatus((prev) => ({ ...prev, camera: false }));
          toast({
            title: "Camera Permission Required",
            description:
              "Please allow camera access to proceed with this exam.",
            variant: "destructive",
          });
        }
      }

      if (requiresAudio) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          setPermissionStatus((prev) => ({ ...prev, audio: true }));
          stream.getTracks().forEach((track) => track.stop());
        } catch (error) {
          console.error("Audio permission error:", error);
          setPermissionStatus((prev) => ({ ...prev, audio: false }));
          toast({
            title: "Microphone Permission Required",
            description:
              "Please allow microphone access to proceed with this exam.",
            variant: "destructive",
          });
        }
      }
    };

    if (exam && isProctoring) {
      requestPermissions();
    }
  }, [exam, isProctoring, requiresCamera, requiresAudio, toast]);

  // Face detection status change handler
  const handleFaceDetectionChange = useCallback((detected) => {
    setFaceDetected(detected);
  }, []);

  // Camera permission status change handler
  const handleCameraPermissionChange = useCallback((granted) => {
    setPermissionStatus((prev) => ({ ...prev, camera: granted }));
  }, []);

  // Audio permission request handler
  const requestAudioPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionStatus((prev) => ({ ...prev, audio: true }));

      // Stop the audio stream after permission is granted
      stream.getAudioTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      console.error("Audio permission error:", error);
      setPermissionStatus((prev) => ({ ...prev, audio: false }));
      throw error;
    }
  }, []);

  // Permission dialog handler
  const handlePermissionRequest = useCallback(async () => {
    try {
      if (permissionType === "audio") {
        await requestAudioPermission();
      } else if (permissionType === "camera") {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        setPermissionStatus((prev) => ({ ...prev, camera: true }));
        stream.getTracks().forEach((track) => track.stop());
      }
      setShowPermissionDialog(false);
    } catch (error) {
      console.error(`${permissionType} permission error:`, error);
      toast({
        title: "Permission Denied",
        description: `${permissionType} access is required for this exam.`,
        variant: "destructive",
      });
    }
  }, [permissionType, requestAudioPermission, toast]);

  // Check if all required permissions are granted
  const allPermissionsGranted = () => {
    if (!isProctoring) return true;

    const cameraOk = !requiresCamera || permissionStatus.camera;
    const audioOk = !requiresAudio || permissionStatus.audio;
    const faceOk =
      !requiresFaceDetection || (permissionStatus.camera && faceDetected);

    return cameraOk && audioOk && faceOk;
  };

  // Start exam handler
  const handleStartExam = async () => {
    if (exam?.proctoring?.enabled) {
      // Check all required permissions before starting
      const permissionsNeeded = [];
      
      if (exam.proctoring.faceDetection && !faceDetected) {
        permissionsNeeded.push("Face detection");
      }
      
      if (exam.proctoring.audioRecording && !audioPermission) {
        permissionsNeeded.push("Audio recording");
      }
      
      if (exam.proctoring.screenRecording && !screenPermission) {
        permissionsNeeded.push("Screen recording");
      }

      if (permissionsNeeded.length > 0) {
        toast({
          title: "Permissions Required",
          description: `Please enable: ${permissionsNeeded.join(", ")}`,
          variant: "destructive",
        });
        return;
      }
    }

    try {
      
      const response = await examCandidateAPI.startSession(assignmentId);
      
      if (response.data?.sessionId) {
        toast({
          title: "Exam Started",
          description: "Good luck with your exam!",
          duration: 3000,
        });
        
        // Navigate to exam session with the session ID
        navigate(`/candidate/exam/${response.data.sessionId}`);
      } else {
        throw new Error("No session ID received from server");
      }
    } catch (error) {
      console.error("Error starting exam:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to start exam",
        variant: "destructive",
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-16rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary border-t-primary"></div>
      </div>
    );
  }

  // Error state
  if (error || !exam || !assignment) {
    return (
      <div className="container py-10">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.response?.data?.message ||
              "Failed to load exam details. Please contact an administrator."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-10 max-w-4xl">
      {/* Render camera component only if face detection is required */}
      {requiresFaceDetection && (
        <FaceDetectionCamera
          onFaceDetectionChange={handleFaceDetectionChange}
          onPermissionStatusChange={handleCameraPermissionChange}
        />
      )}

      <div className="flex flex-col items-center mb-8 text-center">
        <h1 className="text-3xl font-bold mb-2">{exam.name}</h1>
        <Badge className="mb-2">{exam.language || "English"}</Badge>
        <div className="flex items-center text-gray-600">
          <Clock className="mr-1 h-4 w-4" />
          <span>Duration: {assignment.totalTimeAllotted} minutes</span>
        </div>
      </div>

      {/* Exam Instructions Card */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Exam Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          {exam.instructions ? (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: exam.instructions }}
            />
          ) : (
            <div className="space-y-4">
              <p>
                Please read the following instructions carefully before starting
                the exam:
              </p>

              <div className="space-y-2">
                <h3 className="font-medium">General Instructions:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    Total time for this exam is {assignment.totalTimeAllotted}{" "}
                    minutes.
                  </li>
                  <li>
                    You must complete the exam in one sitting. Do not close the
                    browser window.
                  </li>
                  <li>
                    All questions are mandatory unless specified otherwise.
                  </li>
                  <li>
                    You can review and change your answers before submission.
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium">Technical Requirements:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Use a reliable internet connection.</li>
                  <li>
                    Use a desktop or laptop computer for the best experience.
                  </li>
                  <li>
                    Use the latest version of Chrome, Firefox, Edge, or Safari
                    browsers.
                  </li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proctoring Information Card */}
      {isProctoring && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Proctoring Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Alert className="bg-yellow-50 border-yellow-200 mb-4">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertTitle className="text-yellow-800">
                  Important Notice
                </AlertTitle>
                <AlertDescription className="text-yellow-700">
                  This exam uses proctoring technology. Any suspicious activity
                  may result in disqualification after {exam.warningsAllowed}{" "}
                  warning(s).
                </AlertDescription>
              </Alert>

              <p>
                This exam is proctored with the following monitoring enabled:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {requiresCamera && (
                  <div
                    className={`flex items-center p-3 border rounded-md ${
                      permissionStatus.camera
                        ? "border-green-300 bg-green-50"
                        : "border-red-300 bg-red-50"
                    }`}
                  >
                    <div
                      className={`${
                        permissionStatus.camera ? "bg-green-100" : "bg-red-100"
                      } p-2 rounded-full mr-3`}
                    >
                      <Camera
                        className={`h-5 w-5 ${
                          permissionStatus.camera
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      />
                    </div>
                    <div>
                      <h3 className="font-medium">Webcam Monitoring</h3>
                      <p className="text-sm text-gray-500">
                        {permissionStatus.camera
                          ? `✓ Camera access granted ${
                              requiresFaceDetection && faceDetected
                                ? "✓ Face detected"
                                : requiresFaceDetection
                                ? "⚠️ No face detected"
                                : ""
                            }`
                          : "❌ Camera access required"}
                      </p>
                    </div>
                  </div>
                )}

                {requiresAudio && (
                  <div
                    className={`flex items-center p-3 border rounded-md ${
                      permissionStatus.audio
                        ? "border-green-300 bg-green-50"
                        : "border-red-300 bg-red-50"
                    }`}
                  >
                    <div
                      className={`${
                        permissionStatus.audio ? "bg-green-100" : "bg-red-100"
                      } p-2 rounded-full mr-3`}
                    >
                      <FileVideo
                        className={`h-5 w-5 ${
                          permissionStatus.audio
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      />
                    </div>
                    <div>
                      <h3 className="font-medium">Audio Recording</h3>
                      <p className="text-sm text-gray-500">
                        {permissionStatus.audio
                          ? "✓ Audio access granted"
                          : "❌ Audio access required"}
                      </p>
                    </div>
                  </div>
                )}

                {exam?.proctoring?.captureImages && (
                  <div className="flex items-center p-3 border rounded-md border-blue-300 bg-blue-50">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <MonitorUp className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">Screen Capture</h3>
                      <p className="text-sm text-blue-700">
                        <strong>Entire screen sharing required</strong> - Window or tab sharing is not allowed
                      </p>
                    </div>
                  </div>
                )}

                {exam.proctoring.trackTabSwitching && (
                  <div className="flex items-center p-3 border rounded-md border-gray-300">
                    <div className="bg-yellow-100 p-2 rounded-full mr-3">
                      <MonitorUp className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">Tab Switching Detection</h3>
                      <p className="text-sm text-gray-500">
                        Changing browser tabs will be detected
                      </p>
                    </div>
                  </div>
                )}

                {requiresFaceDetection && (
                  <div className="flex items-center p-3 border rounded-md border-gray-300">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <Eye className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">Face Detection</h3>
                      <p className="text-sm text-gray-500">
                        Your face must be visible during the exam
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center p-3 border rounded-md border-gray-300">
                  <div className="bg-red-100 p-2 rounded-full mr-3">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Warning System</h3>
                    <p className="text-sm text-gray-500">
                      {exam.warningsAllowed > 0
                        ? `You will be disqualified after ${exam.warningsAllowed} warning(s)`
                        : "Any suspicious activity will result in immediate disqualification"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Permission Issues Warning */}
      {isProctoring && !allPermissionsGranted() && (
        <Card className="mb-8 border-red-300 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-red-800 mb-1">
                  Permission Issues Detected
                </h3>
                <p className="text-red-700 mb-3">
                  {!permissionStatus.camera &&
                    requiresCamera &&
                    "Camera access is required. "}
                  {!permissionStatus.audio &&
                    requiresAudio &&
                    "Microphone access is required. "}
                  {requiresFaceDetection &&
                    permissionStatus.camera &&
                    !faceDetected &&
                    "Your face must be clearly visible. "}
                  Please resolve these issues before starting the exam.
                </p>
                <div className="flex flex-wrap gap-2">
                  {!permissionStatus.camera && requiresCamera && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPermissionType("camera");
                        setShowPermissionDialog(true);
                      }}
                    >
                      Enable Camera
                    </Button>
                  )}
                  {!permissionStatus.audio && requiresAudio && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPermissionType("audio");
                        setShowPermissionDialog(true);
                      }}
                    >
                      Enable Microphone
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terms checkbox */}
      <div className="flex items-start space-x-2 mb-8">
        <Checkbox
          id="terms"
          checked={agreeToTerms}
          onCheckedChange={(checked) => setAgreeToTerms(checked === true)}
        />
        <div className="grid gap-1.5 leading-none">
          <Label
            htmlFor="terms"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I confirm that
          </Label>
          <p className="text-sm text-gray-500">
            I have read all instructions, I will not engage in any malpractice,
            and I agree to the proctoring measures.
          </p>
        </div>
      </div>

      {/* Start button */}
      <div className="flex justify-center">
        <Button
          size="lg"
          disabled={!agreeToTerms || startingExam || !allPermissionsGranted()}
          onClick={handleStartExam}
          className="w-full max-w-xs"
        >
          {startingExam ? "Starting Exam..." : "Start Exam"}
          {!startingExam && <Check className="ml-2 h-4 w-4" />}
        </Button>
      </div>

      {/* Status message */}
      {!allPermissionsGranted() && (
        <div className="mt-4 text-center text-red-500">
          <p>Please resolve all permission issues before starting the exam</p>
        </div>
      )}

      {/* Permission dialog */}
      <Dialog
        open={showPermissionDialog}
        onOpenChange={setShowPermissionDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Allow {permissionType === "camera" ? "Camera" : "Microphone"}{" "}
              Access
            </DialogTitle>
            <DialogDescription>
              This exam requires{" "}
              {permissionType === "camera" ? "camera" : "microphone"} access for
              proctoring purposes. Please click "Allow" when prompted by your
              browser.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex justify-center">
              {permissionType === "camera" ? (
                <Camera className="h-16 w-16 text-blue-500" />
              ) : (
                <FileVideo className="h-16 w-16 text-purple-500" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handlePermissionRequest}>
              Request Permission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExamInstructions;
